import React, {
    HTMLAttributes,
    ReactHTML,
    useEffect,
    useRef,
    useState
} from "react";
import "./Draggable.scss";
import { Portal } from "../../containers/Portal/Portal";

export let DraggedElement: undefined | HTMLDivElement;

interface Position {
    x: number
    y: number
}

interface DraggingState {
    dragging: true
    startPosition: Position
    position: Position
}

type DragState = {
    dragging: false
    startPosition?: Position
    position?: Position
} | DraggingState;

// @ts-ignore
interface IProps extends HTMLAttributes<HTMLElement> {
    element?: keyof ReactHTML
    container?: HTMLElement
    onDragStart?: (elm: HTMLElement, event: PointerEvent) => void
    onDrag?: (elm: HTMLElement, event: PointerEvent) => void
    onDragStop?: (elm: HTMLElement, event: PointerEvent) => void
}

export function Draggable({
    element = 'div',
    children,
                              container,
                              onDragStart,
                              onDrag,
                              onDragStop,
    style,
    className = '',
    ...otherProps
                          }: IProps) {
    const ref = useRef<HTMLElement>(null);
    const helperRef = useRef<HTMLElement>(null);
    const [dragState, setDragState] = useState<DragState>({ dragging: false });

    // Handle the dragging
    useEffect(() => {
        if (!ref.current) return;
        const div = ref.current as HTMLDivElement;
        const body = document.querySelector('body') as HTMLBodyElement;

        const handleDragStart = (e: PointerEvent) => {
            const {clientX, clientY} = e;
            (e.target as HTMLDivElement).releasePointerCapture(e.pointerId);
            body.style.userSelect = 'none';

            DraggedElement = div;

            const position = {x: clientX, y: clientY};
            setDragState({
                dragging: true,
                position,
                startPosition: position,
            });
        };
        const handleDragMove = (e: PointerEvent) => {
            if (!dragState.dragging) return;
            const {clientX, clientY} = e;
            setDragState((prevState) => ({
                ...prevState,
                position: {x: clientX, y: clientY}
            }));
        };
        const handleDragEnd = () => {
            if (!dragState.dragging) return;
            body.style.userSelect = 'revert';
            setDragState({ dragging: false });
            DraggedElement = undefined;
        };

        const preventNativeDrag = (event: TouchEvent) => {
            event.stopPropagation();
            event.preventDefault();
        }
        div.addEventListener('touchstart', preventNativeDrag);

        div.addEventListener('pointerdown', handleDragStart);
        window.addEventListener('pointermove', handleDragMove);
        window.addEventListener('pointerup', handleDragEnd);

        return () => {
            div.addEventListener('touchstart', preventNativeDrag);
            div.removeEventListener('pointerdown', handleDragStart);
            window.removeEventListener('pointermove', handleDragMove);
            window.removeEventListener('pointerup', handleDragEnd);
        }
    }, [ref, dragState.dragging]);

    // // Handle the dropping
    // useEffect(() => {
    //     if (!wrapperRef.current || !ref.current) return;
    //     const dragElement = ref.current as HTMLDivElement;
    //     const dropZone = wrapperRef.current as HTMLDivElement;
    //     dropZone.classList.toggle('is-dragging', dragState.dragging);
    //
    //     if (!dragState.dragging) return;
    //
    //     const dropElements = dropZone.querySelectorAll('.is-droppable');
    //     const functions: any[] = [];
    //     dropElements.forEach((child, index) => {
    //         const div = child as HTMLDivElement;
    //         functions.push(() => {
    //             onDrop(dragElement, div)
    //         });
    //         div.addEventListener('pointerup', functions[index]);
    //     });
    //
    //     return () => {
    //         dropElements.forEach((child, index) => {
    //             const div = child as HTMLDivElement;
    //             div.removeEventListener('pointerup', functions[index]);
    //         });
    //     }
    //
    // }, [wrapperRef, ref, dragState.dragging, onDrop]);

    const args = {
        className: `${className} draggable-wrapper`,
        style: {
            ...style,
        },
        ...otherProps,
    };
    const rect = ref?.current?.getBoundingClientRect();

    const portalChild = React.createElement(element, {
            ...args,
            ...style,
            ...otherProps
        }, children)

    return <>
        {React.createElement(element, {
            ref,
            ...args,
            style: {
                ...args.style,
                ...(dragState.dragging ? {
                    visibility: 'hidden'
                }: {})
            }
        }, children)}
        <Portal>
            {React.createElement('div', {
                ref: helperRef,
                className: [
                    'draggable-wrapper',
                    dragState.dragging ? 'is-dragging' : ''
                ].join(' '),
                style: {
                    position: 'absolute',
                    pointerEvents: 'none',
                    visibility: 'hidden',
                    ...(dragState.dragging ? {
                        zIndex: 10000,
                        visibility: 'visible',
                        transform: `translate(${dragState.position.x - dragState.startPosition.x}px, ${dragState.position.y - dragState.startPosition.y}px)`,
                    } : {}),
                    ...(rect ? {
                        width: `${rect.width}px`,
                        height: `${rect.height}px`,
                        top: `${rect.top}px`,
                        left: `${rect.left}px`,
                    } : {}),
                }
            }, portalChild)}
        </Portal>
    </>
}
